import * as React from 'react'
import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { config } from 'react-awesome-styled-grid'
import { HeaderSeeAll } from '@components/shared/HeaderSeeAll'
import { ReviewCard } from './ReviewCard'
import { getChapterReviews } from '../../services/userReviews'
import type { TrendingReviewType } from '../../services/userReviews'

const MIN_REVIEWS_TO_SHOW = 5

type ListenerReviewsSectionPropsType = {
  chapterId: string,
  title?: string,
  description?: string | null
}

type ReviewsStateType = {
  status: 'idle' | 'loading' | 'success' | 'error',
  reviews: TrendingReviewType[],
  error: Error | null
}

export function ListenerReviewsSection ({
  chapterId,
  title = 'What Listeners Are Saying',
  description = null
}: ListenerReviewsSectionPropsType): React.ReactNode {
  const [state, setState] = useState<ReviewsStateType>({
    status: 'idle',
    reviews: [],
    error: null
  })

  useEffect((): (() => void) => {
    let isMounted = true

    async function fetchReviews (): Promise<void> {
      setState((prev: ReviewsStateType): ReviewsStateType => ({ ...prev, status: 'loading' }))

      try {
        const response = await getChapterReviews(chapterId, {
          pageSize: 20
        })

        if (isMounted) {
          setState({
            status: 'success',
            reviews: response.reviews,
            error: null
          })
        }
      } catch (error) {
        if (isMounted) {
          setState({
            status: 'error',
            reviews: [],
            error: error instanceof Error ? error : new Error('Failed to fetch reviews')
          })
        }
      }
    }

    fetchReviews()

    return (): void => {
      isMounted = false
    }
  }, [chapterId])

  // Don't render anything while loading or if there's an error
  if (state.status === 'idle' || state.status === 'loading') {
    return null
  }

  if (state.status === 'error') {
    return null
  }

  // Only render if we have enough reviews
  if (state.reviews.length < MIN_REVIEWS_TO_SHOW) {
    return null
  }

  return (
    <SectionContainer>
      <HeaderSeeAll title={title} description={description} />
      <ReviewsRow>
        {state.reviews.map((review: TrendingReviewType): React.ReactNode => (
          <ReviewCardWrapper key={review.id}>
            <ReviewCard review={review} />
          </ReviewCardWrapper>
        ))}
      </ReviewsRow>
    </SectionContainer>
  )
}

const SectionContainer = styled.div<any>`
  width: 100%;
`

const ReviewsRow = styled.div<any>`
  display: flex;
  gap: 12px;
  width: 100%;
  overflow-x: scroll;
  overflow-y: hidden;
  margin: 0;
  padding: 0 0 8px 0;

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  ${(props: any): any => config(props).media.md`
    gap: 16px;
  `}
  ${(props: any): any => config(props).media.lg`
    gap: 20px;
  `}
`

const ReviewCardWrapper = styled.div<any>`
  flex-shrink: 0;
  width: 280px;

  ${(props: any): any => config(props).media.sm`
    width: 300px;
  `}
  ${(props: any): any => config(props).media.md`
    width: 320px;
  `}
  ${(props: any): any => config(props).media.lg`
    width: 340px;
  `}
`

export default ListenerReviewsSection
